// client/src/utils/cityHelper.js
// Utility functions for handling bilingual city names

import { GEORGIAN_CITIES } from '../constants/formData';

/**
 * Get the display name for a city in the specified language
 * @param {string} cityKey - The English city name (used as key)
 * @param {string} language - 'en' or 'ka'
 * @returns {string} The city name in the requested language
 */
export const getCityDisplayName = (cityKey, language = 'en') => {
    if (!cityKey) return '';

    // Find the city object in our constants
    const cityObj = GEORGIAN_CITIES.find(city => city.en === cityKey);

    if (!cityObj) {
        // If not found in our list, return the key as-is
        return cityKey;
    }

    // Return the name in the requested language
    return cityObj[language] || cityObj.en;
};

/**
 * Get display names for multiple cities
 * @param {Array<string>} cityKeys - Array of English city names
 * @param {string} language - 'en' or 'ka'
 * @returns {Array<string>} Array of translated city names
 */
export const getCitiesDisplayNames = (cityKeys, language = 'en') => {
    if (!Array.isArray(cityKeys)) return [];

    return cityKeys.map(key => getCityDisplayName(key, language));
};

/**
 * Get the first city and count for display (similar to categories approach)
 * @param {Array<string>} cities - Array of city keys
 * @param {string} language - 'en' or 'ka'
 * @returns {Object} { firstCity: string, additionalCount: number }
 */
export const getFirstCityWithCount = (cities, language = 'en') => {
    if (!Array.isArray(cities) || cities.length === 0) {
        return { firstCity: '', additionalCount: 0 };
    }

    const firstCity = getCityDisplayName(cities[0], language);
    const additionalCount = cities.length > 1 ? cities.length - 1 : 0;

    return { firstCity, additionalCount };
};
